import { useMutation } from 'react-apollo';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import {
  yearbookPhotosSet as yearbookPhotosSetMutation,
  yearbookPhotoDelete as yearbookPhotoDeleteMutation,
} from '../../../graphql/mutations/yearbook-photos';
import {
  MatchedPhotoYearbookPhotoDetails,
  IdentityYearbookPhotoDetails,
} from '../../../graphql/fragments/yearbook-photos';

const useYearbookPhotoMutations = () => {
  const [yearbookPhotosSet] = useMutation(yearbookPhotosSetMutation);
  const [yearbookPhotoDelete] = useMutation(yearbookPhotoDeleteMutation);

  const onYearbookPhotosSet = async ({
    input,
    setMatchedPhotoId,
    existingMatchedPhotoId,
    onUpdateStore,
    mutationProps = {},
  }) => {
    await yearbookPhotosSet({
      variables: {
        input,
      },
      update: (
        store,
        {
          data: {
            yearbookPhotosSet: { yearbook_photos: yearbookPhotos },
          },
        },
      ) => {
        try {
          input.yearbook_photos.forEach(yearbookPhotoInput => {
            const identityYearbookPhotoFragment = store.readFragment({
              id: defaultDataIdFromObject({
                id: yearbookPhotoInput.identity_id,
                __typename: 'Identity',
              }),
              fragment: IdentityYearbookPhotoDetails,
              fragmentName: 'IdentityYearbookPhotoDetails',
              variables: {
                album_folder_id: input.album_folder_id,
              },
            });

            identityYearbookPhotoFragment.yearbook_photo = yearbookPhotos.find(
              ybPhoto => ybPhoto.identity.id === yearbookPhotoInput.identity_id,
            );

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: yearbookPhotoInput.identity_id,
                __typename: 'Identity',
              }),
              fragment: IdentityYearbookPhotoDetails,
              fragmentName: 'IdentityYearbookPhotoDetails',
              variables: {
                album_folder_id: input.album_folder_id,
              },
              data: identityYearbookPhotoFragment,
            });
          });
        } catch {
          // Do nothing, query might not be loaded
        }

        if (existingMatchedPhotoId) {
          try {
            const existingMatchedPhotoYearbookPhotoFragment =
              store.readFragment({
                id: defaultDataIdFromObject({
                  id: existingMatchedPhotoId,
                  __typename: 'MatchedPhoto',
                }),
                fragment: MatchedPhotoYearbookPhotoDetails,
                fragmentName: 'MatchedPhotoYearbookPhotoDetails',
              });

            existingMatchedPhotoYearbookPhotoFragment.yearbook_photo = null;

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: existingMatchedPhotoId,
                __typename: 'MatchedPhoto',
              }),
              fragment: MatchedPhotoYearbookPhotoDetails,
              fragmentName: 'MatchedPhotoYearbookPhotoDetails',
              data: existingMatchedPhotoYearbookPhotoFragment,
            });
          } catch {
            // Do nothing, query might not be loaded
          }
        }

        if (setMatchedPhotoId) {
          try {
            const setMatchedPhotoYearbookPhotoFragment = store.readFragment({
              id: defaultDataIdFromObject({
                id: setMatchedPhotoId,
                __typename: 'MatchedPhoto',
              }),
              fragment: MatchedPhotoYearbookPhotoDetails,
              fragmentName: 'MatchedPhotoYearbookPhotoDetails',
            });

            setMatchedPhotoYearbookPhotoFragment.yearbook_photo =
              yearbookPhotos?.[0];

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: setMatchedPhotoId,
                __typename: 'MatchedPhoto',
              }),
              fragment: MatchedPhotoYearbookPhotoDetails,
              fragmentName: 'MatchedPhotoYearbookPhotoDetails',
              data: setMatchedPhotoYearbookPhotoFragment,
            });
          } catch {
            // Do nothing, query might not be loaded
          }
        }

        onUpdateStore?.(store);
      },
      ...mutationProps,
    });
  };

  const onYearbookPhotoDelete = async ({ input, existingMatchedPhotoId }) => {
    await yearbookPhotoDelete({
      variables: {
        input,
      },
      update: store => {
        try {
          const identityYearbookPhotoFragment = store.readFragment({
            id: defaultDataIdFromObject({
              id: input.identity_id,
              __typename: 'Identity',
            }),
            fragment: IdentityYearbookPhotoDetails,
            fragmentName: 'IdentityYearbookPhotoDetails',
            variables: {
              album_folder_id: input.album_folder_id,
            },
          });

          identityYearbookPhotoFragment.yearbook_photo = null;

          store.writeFragment({
            id: defaultDataIdFromObject({
              id: input.identity_id,
              __typename: 'Identity',
            }),
            fragment: IdentityYearbookPhotoDetails,
            fragmentName: 'IdentityYearbookPhotoDetails',
            variables: {
              album_folder_id: input.album_folder_id,
            },
            data: identityYearbookPhotoFragment,
          });
        } catch {
          //   Nothing to do, query might not be loaded
        }
        if (existingMatchedPhotoId) {
          try {
            const existingMatchedPhotoYearbookPhotoFragment =
              store.readFragment({
                id: defaultDataIdFromObject({
                  id: existingMatchedPhotoId,
                  __typename: 'MatchedPhoto',
                }),
                fragment: MatchedPhotoYearbookPhotoDetails,
                fragmentName: 'MatchedPhotoYearbookPhotoDetails',
              });

            existingMatchedPhotoYearbookPhotoFragment.yearbook_photo = null;

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: existingMatchedPhotoId,
                __typename: 'MatchedPhoto',
              }),
              fragment: MatchedPhotoYearbookPhotoDetails,
              fragmentName: 'MatchedPhotoYearbookPhotoDetails',
              data: existingMatchedPhotoYearbookPhotoFragment,
            });
          } catch {
            // Do nothing, query might not be loaded
          }
        }
      },
    });
  };

  return {
    onYearbookPhotosSet,
    onYearbookPhotoDelete,
  };
};

export default useYearbookPhotoMutations;
